<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import WoocommerceAPI from 'dashboard/api/integrations/woocommerce';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
  showAddButton: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['delete', 'add']);
const { t } = useI18n();

const { integration, isHookTypeInbox, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const globalConfig = useMapGetter('globalConfig/get');
const searchQuery = ref('');

const hookHeaders = computed(() => {
  const headers = [...(integration.value.visible_properties || [])];
  if (isHookTypeInbox.value) {
    headers.push(t('INTEGRATION_APPS.LIST.INBOX'));
  }
  headers.push(t('INTEGRATION_APPS.LIST.ACTIONS'));
  return headers;
});

const hooks = computed(() => {
  if (!hasConnectedHooks.value) {
    return [];
  }
  const { hooks: integrationHooks } = integration.value;
  const visibleProperties = integration.value.visible_properties || [];
  return integrationHooks.map(hook => ({
    ...hook,
    id: hook.id,
    properties: visibleProperties.map(property => ({
      name: property,
      value: hook.settings[property] ? hook.settings[property] : '--',
    })),
  }));
});

const toDisplayValue = value => {
  if (value === null || value === undefined) {
    return '--';
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return String(value);
};

const filteredHooks = computed(() => {
  const query = searchQuery.value?.trim() || '';
  if (!query) return hooks.value;
  const lowerQuery = query.toLowerCase();
  return (
    hooks.value?.filter(hook =>
      hook.properties?.some(prop =>
        toDisplayValue(prop?.value)?.toLowerCase().includes(lowerQuery)
      )
    ) || []
  );
});

const inboxName = hook => (hook.inbox ? hook.inbox.name : '');
const isEcommerce = computed(() => props.integrationId === 'woocommerce');
const canCopyProperty = property =>
  property.name === 'webhook_url' && property.value !== '--';
const copyPropertyValue = async property => {
  await copyTextToClipboard(property.value);
  useAlert(t('INTEGRATION_APPS.LIST.COPY_SUCCESS'));
};

const showSyncModal = ref(false);
const selectedHook = ref(null);
const previewCount = ref(0);
const isLoadingPreview = ref(false);
const isStartingSync = ref(false);
const hasStartTriggered = ref(false);
const syncStatus = ref({
  state: 'idle',
  total_contacts: 0,
  imported_contacts: 0,
  failed_contacts: 0,
  error: null,
});
const pollTimer = ref(null);

const isSyncInProgress = computed(() =>
  ['queued', 'running'].includes(syncStatus.value.state)
);
const isSyncCompleted = computed(() => syncStatus.value.state === 'completed');
const primaryButtonLabel = computed(() =>
  isSyncCompleted.value
    ? t('INTEGRATION_APPS.SYNC_CONTACTS.CLOSE')
    : t('INTEGRATION_APPS.SYNC_CONTACTS.START')
);
const progressPercent = computed(() => {
  const total = syncStatus.value.total_contacts || previewCount.value || 0;
  if (!total) return 0;
  return Math.round(((syncStatus.value.imported_contacts || 0) / total) * 100);
});

const clearPolling = () => {
  if (pollTimer.value) {
    clearInterval(pollTimer.value);
    pollTimer.value = null;
  }
};

const fetchSyncStatus = async () => {
  if (!selectedHook.value) return;
  try {
    const response = await WoocommerceAPI.syncStatus(selectedHook.value.id);
    syncStatus.value = response.data;
    if (!['queued', 'running'].includes(syncStatus.value.state)) {
      clearPolling();
    }
  } catch (error) {
    clearPolling();
  }
};

const startPolling = () => {
  clearPolling();
  fetchSyncStatus();
  pollTimer.value = setInterval(fetchSyncStatus, 1000);
};

const openSyncModal = async hook => {
  selectedHook.value = hook;
  showSyncModal.value = true;
  isLoadingPreview.value = true;
  hasStartTriggered.value = false;
  syncStatus.value = {
    state: 'idle',
    total_contacts: 0,
    imported_contacts: 0,
    failed_contacts: 0,
    error: null,
  };

  try {
    const [previewResponse] = await Promise.all([
      WoocommerceAPI.syncPreview(hook.id),
      fetchSyncStatus(),
    ]);
    previewCount.value = previewResponse.data.total_contacts || 0;
    if (isSyncInProgress.value) startPolling();
  } catch (error) {
    const message =
      error?.response?.data?.error || t('INTEGRATION_APPS.SYNC_CONTACTS.ERROR');
    useAlert(message);
  } finally {
    isLoadingPreview.value = false;
  }
};

const closeSyncModal = () => {
  showSyncModal.value = false;
  selectedHook.value = null;
  previewCount.value = 0;
  hasStartTriggered.value = false;
  clearPolling();
};

const startSync = async () => {
  if (!selectedHook.value || hasStartTriggered.value) return;

  hasStartTriggered.value = true;
  isStartingSync.value = true;
  try {
    await WoocommerceAPI.syncStart(selectedHook.value.id);
    startPolling();
  } catch (error) {
    hasStartTriggered.value = false;
    const message =
      error?.response?.data?.error || t('INTEGRATION_APPS.SYNC_CONTACTS.ERROR');
    useAlert(message);
  } finally {
    isStartingSync.value = false;
  }
};

const handlePrimaryButton = () => {
  if (isSyncCompleted.value) {
    closeSyncModal();
    return;
  }

  startSync();
};

onBeforeUnmount(() => {
  clearPolling();
});
</script>

<template>
  <div class="flex flex-col flex-1 gap-4 overflow-auto">
    <BaseSettingsHeader
      v-model:search-query="searchQuery"
      :title="integration.name || ''"
      :description="
        $t(
          `INTEGRATION_APPS.SIDEBAR_DESCRIPTION.${integration.name.toUpperCase()}`,
          { installationName: globalConfig.installationName }
        )
      "
      :feature-name="integrationId"
      :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      :search-placeholder="$t('INTEGRATION_APPS.SEARCH_PLACEHOLDER')"
    >
      <template v-if="hooks?.length" #count>
        <span class="text-body-main text-n-slate-11">
          {{ $t('INTEGRATION_APPS.COUNT', { n: hooks.length }) }}
        </span>
      </template>
      <template #actions>
        <NextButton
          v-if="showAddButton"
          :label="$t('INTEGRATION_APPS.ADD_BUTTON')"
          size="sm"
          @click="$emit('add')"
        />
      </template>
    </BaseSettingsHeader>
    <div class="w-full">
      <BaseTable
        v-if="hasConnectedHooks"
        :headers="hookHeaders"
        :items="filteredHooks"
        :no-data-message="searchQuery ? $t('INTEGRATION_APPS.NO_RESULTS') : ''"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="hook in items" :key="hook.id" :item="hook">
            <template #default>
              <BaseTableCell
                v-for="property in hook.properties"
                :key="`${property.name}-${property.value}`"
              >
                <div class="flex items-center gap-2">
                  <span class="text-body-main text-n-slate-12 break-all">
                    {{ toDisplayValue(property.value) }}
                  </span>
                  <NextButton
                    v-if="canCopyProperty(property)"
                    v-tooltip.top="$t('INTEGRATION_APPS.LIST.COPY')"
                    icon="i-lucide-copy"
                    slate
                    faded
                    sm
                    @click="copyPropertyValue(property)"
                  />
                </div>
              </BaseTableCell>

              <BaseTableCell v-if="isHookTypeInbox">
                <span class="text-body-main text-n-slate-11 break-words">
                  {{ inboxName(hook) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-12">
                <div class="flex justify-end gap-3 flex-shrink-0">
                  <NextButton
                    v-if="isEcommerce"
                    :label="$t('INTEGRATION_APPS.SYNC_CONTACTS.BUTTON')"
                    slate
                    sm
                    @click="openSyncModal(hook)"
                  />
                  <NextButton
                    v-tooltip.top="
                      $t('INTEGRATION_APPS.LIST.DELETE.BUTTON_TEXT')
                    "
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="$emit('delete', hook)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
      <p v-else class="flex flex-col items-center justify-center h-full">
        {{
          $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', {
            integrationId: integration.id,
          })
        }}
      </p>
    </div>
    <woot-modal v-model:show="showSyncModal" @close="closeSyncModal">
      <div class="h-auto overflow-auto flex flex-col">
        <woot-modal-header
          :header-title="$t('INTEGRATION_APPS.SYNC_CONTACTS.TITLE')"
          :header-content="$t('INTEGRATION_APPS.SYNC_CONTACTS.DESCRIPTION')"
        />
        <div class="px-8 pb-6 flex flex-col gap-4">
          <p class="text-sm text-n-slate-11">
            {{
              $t('INTEGRATION_APPS.SYNC_CONTACTS.COUNT', {
                count: previewCount,
              })
            }}
          </p>
          <div class="w-full h-2 rounded bg-n-slate-4 overflow-hidden">
            <div
              class="h-full bg-n-brand transition-all duration-300"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <p class="text-xs text-n-slate-11">
            {{
              $t('INTEGRATION_APPS.SYNC_CONTACTS.PROGRESS', {
                imported: syncStatus.imported_contacts || 0,
                total: syncStatus.total_contacts || previewCount || 0,
              })
            }}
          </p>
          <p
            v-if="syncStatus.state === 'failed'"
            class="text-sm text-n-ruby-11"
          >
            {{ syncStatus.error || $t('INTEGRATION_APPS.SYNC_CONTACTS.ERROR') }}
          </p>
          <p
            v-if="syncStatus.state === 'completed'"
            class="text-sm text-n-teal-11"
          >
            {{ $t('INTEGRATION_APPS.SYNC_CONTACTS.DONE') }}
          </p>
          <div class="flex justify-end gap-2">
            <NextButton
              slate
              faded
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click="closeSyncModal"
            />
            <NextButton
              :label="primaryButtonLabel"
              :is-loading="
                isSyncCompleted ? false : isStartingSync || isLoadingPreview
              "
              :disabled="
                isSyncCompleted
                  ? false
                  : hasStartTriggered || isSyncInProgress || isLoadingPreview
              "
              @click="handlePrimaryButton"
            />
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>
